import { useState } from 'react'
import React from 'react'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { isNil } from 'lodash'

import { useAsyncData, useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NotFound from 'modules/reports/components/NotFound'
import SaveModal, { SAVE_ACTION } from 'modules/reports/components/SaveReportModal'
import ReportsSqlEditor from 'modules/reports/components/SqlEditor'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import ReportStruct from 'modules/reports/lib/ReportStruct'
import type { RouteContextParams } from 'modules/reports/lib/RouterContextParams'
import { ReportEditorStore } from 'modules/reports/lib/Store'
import reportFromTemplate from 'modules/reports/lib/Store/reportFromTemplate'
import { PromptProvider } from 'modules/reports/lib/promptContext'
import dashboards from 'modules/reports/store/collections/dashboards'
import templates from 'modules/reports/store/collections/templates'
import Report from 'modules/reports/store/models/Report'

interface ReportResult {
  store?: ReportEditorStore
  report?: Report
  isFound: boolean
}

const OpenSqlTemplatePage = () => {
  const { company } = useCurrentSession()
  const { loading } = useOutletContext<RouteContextParams>()
  const params = useParams()
  const [search, _] = useSearchParams()
  const { template_slug: templateSlug } = params

  const [sql, setSql] = useState(null)
  const { loading: isFeatureLoading, featureAvailability } = useFeatureAvailability(
    Features.analytics_advanced_reports
  )
  const { dashboard_id: dashboardId } = useParams()

  const { loading: templatesIsLoading } = useAsyncData(
    () => Promise.all([templates.fetch(), dashboards.fetch()]),
    []
  )

  const { isFound, report, store } = useUnsafeMemo<ReportResult>(() => {
    if (templatesIsLoading) return { isFound: false }

    const template = templates.bySlug(templateSlug || '')
    const isFound = !!template

    const dashboard = dashboards.find({ id: dashboardId && parseInt(dashboardId) })

    if (isNil(dashboard)) {
      throw "the selected dashboard doesn't exist"
    }

    const report = template ? reportFromTemplate(template, dashboard) : new Report({})

    const store = new ReportEditorStore({
      report,
      origin: report?.origin && report.origin == 'template' ? 'modified_template' : undefined,
    })

    return {
      store,
      report,
      isFound,
    }
  }, [templateSlug, dashboardId, templatesIsLoading])

  async function fetchSql() {
    if (!isFound || !report) {
      return
    }

    const reportStruct = ReportStruct.buildFromReport(report)
    const newSql = reportStruct.sqlQuery || (await reportStruct.toSql([]))
    setSql(newSql)
  }

  const { loading: sqlLoading } = useUnsafeAsyncData(fetchSql, [report])

  const { track } = useReportTracking(store)

  React.useEffect(() => {
    if (!report) return

    track(trackingEvents.reports.reportsSqlEditVisited, {})
  }, [track, report])

  const onSuccess = () => {
    track(trackingEvents.reports.newSqlReportSaved, {})
    track(trackingEvents.reports.submitReportFromSql, {
      company_id: company.id,
    })
  }

  const isAuthorized = featureAvailability.available

  const showSaveModal = search.get('action') == SAVE_ACTION

  return (
    <ResponsivePage.Body
      loading={sqlLoading || isFeatureLoading || loading || !store}
      error={undefined}
      authorized={isAuthorized}
      showHeader={false}
      variant="full"
    >
      <NotFound notFound={!isFound}>
        <PromptProvider>
          <Box justifyContent="flexEnd">
            {showSaveModal && report && store && <SaveModal report={report} store={store} />}
            {store && <ReportsSqlEditor store={store} sql={sql} />}
          </Box>
        </PromptProvider>
      </NotFound>
    </ResponsivePage.Body>
  )
}

export default OpenSqlTemplatePage
